"use client";

import { useState, useMemo } from "react";
import Image from "next/image";
import Button from "@components/atoms/Button";
import VariantCard from "@components/molecules/VariantCard";
import ModalManager from "@components/molecules/ModalManager";
import VariantOptionCard from "@components/molecules/ShortlistCard/VariantOption/VariantOptionCard";
import { SHORTLIST_LABELS } from "@constants/shortlist";
import { shortlistCardData } from "@components/molecules/ShortlistCard/ShortlistCard.data";
import assetLoader from "@utils/internalImageLoder";
import { PAGE_TYPES, type PageType } from "@constants/pageTypes";
import styles from "./VariantsSection.module.scss";
import modalStyles from "@components/molecules/ShortlistCard/ShortlistCardModals.module.scss";
import Text from "@components/atoms/Text";
import { updateAppBar } from "@hooks/useWebviewCallback";
import { COMPLETE_TRANSPARENT_APPBAR_CONFIG } from "@utils/appbarConfig";
import { trackEventWithCommonProps, EventType } from "@utils/eventTracker";
import type { BuyingIntentData } from "@services/globalIntentService";
import { useGlobalLoader } from "@hooks/useGlobalLoader";
import { useRouter } from "next/navigation";

const { iconPaths } = shortlistCardData;

interface VariantData {
  variantName: string;
  fuelType: string;
  engine: string;
  transmission: string;
  slug: string;
  marketPrice: number | null;
  adspPrice: number;
  ackoDriveSavings: number;
  onRoadPrice: number;
  mileage?: { value: string; unit: string } | string;
  expressDelivery?: boolean;
  expressDeliveryDays?: number;
  standardDeliveryDays?: number;
  status?: string;
  tag?: string;
  variantType?: string;
  variantLevel?: string;
}

interface AppVariantsContentProps {
  allVariants: VariantData[];
  fuelTypes: string[];
  transmissionTypes: string[];
  slug: string;
  cityName: string;
  isElectric?: boolean;
  pageType?: PageType;
  carAvailability?: string;
  brand: string;
  model: string;
  showAllVariants?: boolean;
  buyingIntent?: BuyingIntentData | null;
}

const AppVariantsContent: React.FC<AppVariantsContentProps> = ({
  allVariants,
  fuelTypes,
  transmissionTypes,
  slug,
  cityName,
  isElectric,
  pageType,
  carAvailability,
  brand,
  model,
  showAllVariants = false,
  buyingIntent,
}) => {
  const router = useRouter();
  const { showLoader } = useGlobalLoader();
  const assetBase = assetLoader();


  const [isOpen, setIsOpen] = useState(false);
  const [selectedFuel, setSelectedFuel] = useState<string | null>(null);
  const [selectedTransmission, setSelectedTransmission] = useState<
    string | null
  >(null);

  const DISPLAY_LIMIT = 3;

  const filteredAllVariants = useMemo(() => {
    return allVariants.filter((v) => {
      if (selectedFuel && v.fuelType !== selectedFuel) return false;
      if (selectedTransmission && v.transmission !== selectedTransmission)
        return false;
      return true;
    });
  }, [allVariants, selectedFuel, selectedTransmission]);

  const filteredDisplayedVariants = useMemo(() => {
    if (showAllVariants) return filteredAllVariants;
    return filteredAllVariants.slice(0, DISPLAY_LIMIT);
  }, [filteredAllVariants, showAllVariants]);

  const toggleFuel = (fuel: string) => {
    setSelectedFuel((prev) => (prev === fuel ? null : fuel));
  };

  const toggleTransmission = (trans: string) => {
    setSelectedTransmission((prev) => (prev === trans ? null : trans));
  };

  const remainingCount = filteredAllVariants.length - filteredDisplayedVariants.length;
  const showViewMore = !showAllVariants && remainingCount > 0;

  const handleViewMoreClick = async (slug: string) => {
    showLoader();
    updateAppBar(COMPLETE_TRANSPARENT_APPBAR_CONFIG);
    await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
      pageType: PAGE_TYPES.MM_PAGE,
      additionalProps: {
        action_type: "tap_btn",
        action_name: "tap_btn_widget_cta",
        car_availability: carAvailability,
        vehicle_make: brand,
        vehicle_model: model,
        city_name: cityName,
        Section: "Variant",
        cta_Name: "Variants"
      },
    });
    router.push(`/acko-app/cars/${slug}/variants`);
  }

  const handleCompareClick = async () => {
    showLoader();
    updateAppBar(COMPLETE_TRANSPARENT_APPBAR_CONFIG);
    await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
      pageType: PAGE_TYPES.MM_PAGE,
      additionalProps: {
        action_type: "tap_btn",
        action_name: "tap_btn_add_to_compare",
        vehicle_make: brand,
        vehicle_model: model,
        city_name: cityName,
        Section: "Compare"
      },
    });
    router.push(`/acko-app/compare/${slug}`);
  }

  return (
    <>
      <div className={styles.filterPills}>
        {fuelTypes.map((fuel) => (
          <button
            key={fuel}
            type="button"
            className={`${styles.filterPill} ${selectedFuel === fuel ? styles.filterPillActive : ""}`}
            onClick={() => toggleFuel(fuel)}
          >
            {fuel}
            {selectedFuel === fuel && (
              <Image
                src={`${assetBase}/icons/Close_Dark.svg`}
                alt=""
                width={13}
                height={13}
                className={styles.filterPillCloseIcon}
              />
            )}
          </button>
        ))}
        {fuelTypes.length > 0 && transmissionTypes.length > 0 && (
          <div className={styles.divider} />
        )}
        {transmissionTypes.map((trans) => (
          <button
            key={trans}
            type="button"
            className={`${styles.filterPill} ${selectedTransmission === trans ? styles.filterPillActive : ""}`}
            onClick={() => toggleTransmission(trans)}
          >
            {trans}
            {selectedTransmission === trans && (
              <Image
                src={`${assetBase}/icons/Close_Dark.svg`}
                alt=""
                width={13}
                height={13}
                className={styles.filterPillCloseIcon}
              />
            )}
          </button>
        ))}
      </div>

      <div className={styles.content}>
        <div>
          <div className={styles.appVariantsGrid}>
            {filteredDisplayedVariants.length > 0
              ? filteredDisplayedVariants.map((variant: any, index: number) => {
                const mileage = variant?.mileage
                  ? `${variant?.mileage?.value} ${variant?.mileage?.unit}`
                  : "";

                return (
                  <VariantCard
                    key={variant.slug || index}
                    title={variant?.variantName}
                    fuelType={variant?.fuelType}
                    engineCapacity={`${isElectric ? variant?.engine / 1000 + " kWH" : variant?.engine + " cc"}`}
                    transmission={variant?.transmission}
                    mileage={mileage}
                    price={variant?.adspPrice}
                    originalPrice={variant?.onRoadPrice}
                    savings={variant?.ackoDriveSavings}
                    location={cityName}
                    badgeType={variant?.tag}
                    variantType={variant?.variantType}
                    className={styles.variantCard}
                    isLoggedIn
                    variantSlug={variant?.slug}
                    brandName={brand}
                    modelName={model}
                    modelSlug={slug}
                    isSellable={variant?.status === "sellable"}
                    pageType={pageType}
                    carAvailability={carAvailability}
                    isInApp
                    expressDelivery={variant?.expressDelivery}
                    expressDeliveryDays={variant?.expressDeliveryDays}
                    standardDeliveryDays={variant?.standardDeliveryDays}
                    buyingIntent={buyingIntent}
                  />
                );
              })
              : <Text className={styles.notAvailText}>No variants match your filter.</Text>}
          </div>

          {showViewMore && (
            <div className={styles.viewMore}>
              <Button
                size="ds"
                className={styles.viewMoreLink}
                onClick={() => handleViewMoreClick(slug)}
              >
                {`View ${remainingCount} more variants`}
                <Image
                  src={`${assetBase}/icons/Chevron_right.svg`}
                  alt="Chevron down"
                  width={20}
                  height={20}
                  loading="lazy"
                />
              </Button>
            </div>
          )}
        </div>
      </div>

      {!showAllVariants && (
        <ModalManager
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          title={SHORTLIST_LABELS.SELECT_VARIANT}
          customClass={modalStyles.variantModalContent}
          modalHeaderClass={modalStyles.variantModalHeader}
          modalBodyClass={modalStyles.variantModalBody}
        >
          <div className={modalStyles.compareBlock}>
            <div className={modalStyles.compareIcon}>
              <Image
                src={`${assetBase}${iconPaths.compareCars}`}
                alt=""
                width={24}
                height={24}
              />
            </div>
            <div className={modalStyles.compareText}>
              <span className={modalStyles.compareLabel}>
                {SHORTLIST_LABELS.COMPARE_VARIANTS_LABEL}
              </span>
              <span
                className={modalStyles.compareCta}
                onClick={() => handleCompareClick()}
                role="button"
                tabIndex={0}
              >
                {SHORTLIST_LABELS.COMPARE_VARIANTS_CTA}
                <Image
                  src={`${assetBase}${iconPaths.chevronRightBlue}`}
                  alt=""
                  width={16}
                  height={16}
                />
              </span>
            </div>
          </div>

          {(fuelTypes.length > 0 || transmissionTypes.length > 0) && (
            <div className={modalStyles.filterRow}>
              {fuelTypes.length > 0 && (
                <div className={modalStyles.filterGroup}>
                  {fuelTypes.map((fuel) => (
                    <button
                      key={fuel}
                      className={`${modalStyles.filterChip} ${selectedFuel === fuel ? modalStyles.filterChipActive : ""}`}
                      onClick={() => toggleFuel(fuel)}
                    >
                      {fuel}
                      {selectedFuel === fuel && (
                        <Image
                          src={`${assetBase}/icons/Close_Dark.svg`}
                          alt=""
                          width={13}
                          height={13}
                          className={modalStyles.filterChipCloseIcon}
                        />
                      )}
                    </button>
                  ))}
                </div>
              )}
              {fuelTypes.length > 0 && transmissionTypes.length > 0 && (
                <div className={modalStyles.filterDivider} />
              )}
              {transmissionTypes.length > 0 && (
                <div className={modalStyles.filterGroup}>
                  {transmissionTypes.map((trans) => (
                    <button
                      key={trans}
                      className={`${modalStyles.filterChip} ${selectedTransmission === trans ? modalStyles.filterChipActive : ""}`}
                      onClick={() => toggleTransmission(trans)}
                    >
                      {trans}
                      {selectedTransmission === trans && (
                        <Image
                          src={`${assetBase}/icons/Close_Dark.svg`}
                          alt=""
                          width={13}
                          height={13}
                          className={modalStyles.filterChipCloseIcon}
                        />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className={modalStyles.variantList}>
            {
              filteredAllVariants.length > 0 ?
                filteredAllVariants.map((variant) => (
                  <VariantOptionCard
                    key={variant.slug}
                    variantName={variant.variantName}
                    fuelType={variant.fuelType}
                    transmission={variant.transmission}
                    mileage={
                      typeof variant.mileage === "object"
                        ? variant.mileage
                        : undefined
                    }
                    isSellable={variant.status === "sellable"}
                    adspPrice={variant.adspPrice}
                    marketPrice={variant.marketPrice || 0}
                    savings={variant.ackoDriveSavings}
                    fallbackPrice={variant.onRoadPrice || variant.marketPrice || 0}
                    isExpressDelivery={variant.expressDelivery}
                    // deliveryDate={getDeliveryDateFromDays(
                    //   variant.expressDelivery
                    //     ? variant.expressDeliveryDays
                    //     : variant.standardDeliveryDays
                    // )}
                    isSelected={false}
                    onClick={() => { }}
                  />
                ))
                :
                <Text className={styles.notAvailText}>No variants match your filter.</Text>
            }
          </div>
        </ModalManager>
      )}
    </>
  );
};

export default AppVariantsContent;
